import React from 'react';
import { Button, Card, Col, Form, Input, Layout, Row, Space, Typography, notification } from 'antd';
import { Link, useNavigate } from 'react-router-dom';

import AppLogo from '../../../components/layout/AppLogo';
import AppFooter from '../../../components/layout/AppFooter';
import { useSignUpMutation } from '../../../app/api/authApiSlice';
import { getErrorMessage } from '../../../utils/errorUtil';

import '../../../styles/styles.less';

const { Content } = Layout;
const { Text } = Typography;

interface User {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  phoneNumber: string;
}

const SignUp: React.FC = () => {
  const [signUp, { isLoading }] = useSignUpMutation();
  const [form] = Form.useForm();
  const navigate = useNavigate();

  const handleSignUp = (user: User): void => {
    const seller = {
      email: user.email,
      password: user.password,
      firstName: user.firstName,
      lastName: user.lastName,
      phoneNumber: user.phoneNumber,
    };

    signUp(seller)
      .unwrap()
      .then((response) => {
        form.resetFields();
        notification.success({
          message: 'Account created successfully',
          description:
            'You need to verify your email address to activate your account. Check your inbox for the confirmation link.',
        });
        navigate('/login');
      })
      .catch((error) => {
        console.log(error);
      });
  };

  return (
    <Layout style={{ background: '#f0f2f5' }}>
      <Content>
        <Row justify='center' align='middle' style={{ minHeight: '100vh' }}>
          <Col xs={20} sm={16} md={14} lg={12} xl={8} xxl={6}>
            <Card>
              <Space direction='vertical' size='middle' style={{ display: 'flex' }}>
                <Row justify='center'>
                  <AppLogo size={128} />
                </Row>
                <Row justify='center'>
                  <Text>Create your Seller Account</Text>
                </Row>
                <Form
                  form={form}
                  name='login'
                  initialValues={{ remember: true }}
                  onFinish={handleSignUp}
                  autoComplete='off'
                >
                  <Form.Item
                    label='First Name'
                    name='firstName'
                    labelCol={{ span: 8 }}
                    wrapperCol={{ span: 16 }}
                    rules={[
                      {
                        required: true,
                        message: 'Please input your first name!',
                      },
                    ]}
                  >
                    <Input placeholder='First Name' />
                  </Form.Item>

                  <Form.Item
                    label='Last Name'
                    name='lastName'
                    labelCol={{ span: 8 }}
                    wrapperCol={{ span: 16 }}
                    rules={[
                      {
                        required: true,
                        message: 'Please input your last name!',
                      },
                    ]}
                  >
                    <Input placeholder='Last Name' />
                  </Form.Item>

                  <Form.Item
                    label='Email Address'
                    name='email'
                    labelCol={{ span: 8 }}
                    wrapperCol={{ span: 16 }}
                    rules={[
                      {
                        required: true,
                        message: 'Please input your email address!',
                      },
                    ]}
                  >
                    <Input placeholder='Email Address' />
                  </Form.Item>

                  <Form.Item
                    label='Password'
                    name='password'
                    labelCol={{ span: 8 }}
                    wrapperCol={{ span: 16 }}
                    rules={[
                      {
                        required: true,
                        message: 'Please input your password!',
                      },
                      {
                        pattern:
                          /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&#])[A-Za-z\d@$!%*?&#]{6,}$/,
                        message:
                          'Password must contain at least one uppercase letter, one lowercase letter, one number and one special character',
                      },
                    ]}
                  >
                    <Input.Password placeholder='Password' />
                  </Form.Item>

                  <Form.Item
                    label='Phone Number'
                    name='phoneNumber'
                    labelCol={{ span: 8 }}
                    wrapperCol={{ span: 16 }}
                    rules={[
                      {
                        required: true,
                        message: 'Please input your phone number!',
                      },
                    ]}
                  >
                    <Input placeholder='Phone Number' />
                  </Form.Item>

                  <Form.Item wrapperCol={{ span: 32 }}>
                    <Button type='primary' htmlType='submit' block loading={isLoading}>
                      Sign Up
                    </Button>
                  </Form.Item>
                </Form>
              </Space>
              <Row justify='center' gutter={16}>
                <Col>
                  <Text>Already have an account?</Text>
                </Col>
                <Col>
                  <Link to='/login'>Log In</Link>
                </Col>
              </Row>
            </Card>
            <AppFooter />
          </Col>
        </Row>
      </Content>
    </Layout>
  );
};

export default SignUp;
